import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {Centrifuge, Subscription} from 'centrifuge'
import {useAppDispatch} from '@/hooks/redux'
import {refreshToken} from '@/store/actions/storage'
import {selectJWT} from '@/store/selectors/storage'
import {store} from '@/store'

interface CentrifugeMessage {
  type?: string
  data?: unknown
  timestamp?: number
  [key: string]: unknown
}

interface useCentrifugeSocketProps {
  lastMessage: CentrifugeMessage | null
  unsubscribe: () => void
  subscribe: (events: (message: CentrifugeMessage) => void) => void
  sendMessage: (data: Record<string, unknown>) => void
  setData: Dispatch<SetStateAction<{token: string; id: number} | null>>
  wsRef: MutableRefObject<Centrifuge | null>
}

const isDevelopment = process.env.NODE_ENV === 'development'

const logDebug = (...args: unknown[]) => {
  if (isDevelopment) {
    console.log(...args)
  }
}

const MAX_REFRESH_ATTEMPTS = 3

const useNotificationsSocket = (url: string): useCentrifugeSocketProps => {
  const dispatch = useAppDispatch()
  const [lastMessage, setLastMessage] = useState<CentrifugeMessage | null>(null)
  const [data, setData] = useState<{token: string; id: number} | null>(null)
  const wsRef = useRef<Centrifuge | null>(null)
  const subRef = useRef<Subscription | null>(null)
  const refreshAttemptsRef = useRef<number>(0)
  const pubMapCallbackRef = useRef<
    ((message: CentrifugeMessage) => void) | null
  >(null)

  const subscribe = useCallback((callback: (v: CentrifugeMessage) => void) => {
    pubMapCallbackRef.current = callback

    if (!wsRef.current) return

    if (subRef.current) {
      subRef.current.subscribe()
      return
    }

    const sub = wsRef.current.newSubscription('pub_map_channel')
    subRef.current = sub
    sub
      .on('publication', (ctx) => {
        pubMapCallbackRef.current?.(ctx.data as CentrifugeMessage)
      })
      .on('subscribing', (ctx) => {
        logDebug(`subscribing: ${ctx.code}, ${ctx.reason}`)
      })
      .on('subscribed', () => {})
      .on('unsubscribed', (ctx) => {
        logDebug(`unsubscribed: ${ctx.code}, ${ctx.reason}`)
      })
      .on('error', (ctx) => {
        logDebug('Subscription error:', ctx)
      })
      .subscribe()
  }, [])

  const unsubscribe = useCallback(() => {
    subRef.current?.unsubscribe()
  }, [])

  const sendMessage = useCallback((msgData: Record<string, unknown>) => {
    wsRef.current?.send(JSON.stringify(msgData))
  }, [])

  useEffect(() => {
    if (!data) return

    refreshAttemptsRef.current = 0

    const ws = new Centrifuge(url, {
      token: data.token,
      getToken: async () => {
        if (refreshAttemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
          logDebug('Max refresh attempts reached, disconnecting Centrifuge')
          ws.disconnect()
          throw new Error('Max refresh attempts reached')
        }

        refreshAttemptsRef.current++
        const refreshResult = await dispatch(refreshToken())

        if (refreshResult.payload) {
          refreshAttemptsRef.current = 0
          const newToken = selectJWT(store.getState())
          if (newToken) return newToken
        }

        throw new Error('Failed to refresh token')
      },
    })

    wsRef.current = ws

    let userSub: Subscription | null = null

    ws.on('connecting', () => {})
      .on('connected', (ctx) => {
        logDebug(`connected over ${ctx.transport}`)

        if (!userSub) {
          userSub = ws.newSubscription(`user:${data.id}`)
          userSub
            .on('publication', (pubCtx) => {
              if (pubCtx?.data) setLastMessage(pubCtx.data as CentrifugeMessage)
            })
            .on('subscribing', (subCtx) => {
              logDebug(`subscribing: ${subCtx.code}, ${subCtx.reason}`)
            })
            .on('subscribed', () => logDebug('subscribed'))
            .on('unsubscribed', (subCtx) => {
              logDebug(`unsubscribed: ${subCtx.code}, ${subCtx.reason}`)
            })
            .subscribe()
        }

        if (pubMapCallbackRef.current && !subRef.current) {
          const sub = ws.newSubscription('pub_map_channel')
          subRef.current = sub
          sub
            .on('publication', (pubCtx) => {
              pubMapCallbackRef.current?.(pubCtx.data as CentrifugeMessage)
            })
            .on('subscribing', (subCtx) => {
              logDebug(`subscribing: ${subCtx.code}, ${subCtx.reason}`)
            })
            .on('subscribed', () => {})
            .on('unsubscribed', (subCtx) => {
              logDebug(`unsubscribed: ${subCtx.code}, ${subCtx.reason}`)
            })
            .subscribe()
        }
      })
      .on('disconnected', (ctx) => logDebug('disconnected', ctx))
      .on('error', (e) => logDebug('Connection error:', e))
      .connect()

    return () => {
      if (subRef.current) {
        subRef.current.unsubscribe()
        subRef.current.removeAllListeners()
        subRef.current = null
      }
      ws.disconnect()
      wsRef.current = null
    }
  }, [data?.id, data?.token, dispatch, url])

  return {sendMessage, unsubscribe, subscribe, lastMessage, setData, wsRef}
}

export default useNotificationsSocket
